<script lang="ts">
	import { createEventDispatcher, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { location, push, replace } from 'svelte-spa-router';
	//Helpers
	import {
		addDashboard,
		deleteDashboard,
		checkIsEventLogTab,
		requestChangeDashboardName,
		getImages,
		getImagesByType,
	} from '../list-helpers';
	// Stores
	import { dashboards, dashboardTabs } from 'src/stores/features/dashboards';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { NON_PREMIUM_DASH_TABS_LIMIT, PREMIUM_DASH_TABS_LIMIT } from 'src/constants';
	// Components
	import { Button, Modal, Text, Container, Input, Icon, Alert } from '@Shelly/shelly-design-system';
	import ImageList from '../ImageList.svelte';
	// Types
	import type { TypeTab } from '../../../types/Tabs';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		createNewDashWithDefaultWidgets,
		DEFAULT_DASH_ID,
		mapDashToAvatar,
	} from 'src/components/Dashboards/dash-helpers';
	const dispatch = createEventDispatcher();
	const isPremium = $user_data.premium || $user_settings.been_premium;
	const DASHBOARDS_LIMIT = isPremium ? PREMIUM_DASH_TABS_LIMIT : NON_PREMIUM_DASH_TABS_LIMIT;

	const alertMessegaLimitTranslation = isPremium
		? 'alert_premium_dashboard_limit_reached'
		: 'alert_basic_dashboard_limit_reached';

	const alertMessageReachedLimitDefault = isPremium
		? `Dashboard limit of ${PREMIUM_DASH_TABS_LIMIT} reached! Remove some of your dashboards to add a new one.`
		: `Dashboard limit of ${NON_PREMIUM_DASH_TABS_LIMIT} reached! Upgrade to Premium to create up to ${PREMIUM_DASH_TABS_LIMIT} dashboards.`;

	const plan = isPremium ? $format('label_premium') : $format('label_basic_plan');

	let dashToDelete = null;
	let editDashId: string | null = null;

	export let dashboardsEdit = false;
	export let editDashObj: any = {
		name: '',
		image: '',
		icon: 'fat fa-objects-column',
	};
	export let showDeleteModal = false;
	export let isEditDash = false;

	$: if (isEditDash || showDeleteModal) {
		const parts = $location.split('/');
		editDashId = parts[parts.length - 1];

		const dashToEdit = $dashboards[editDashId];
		const visual = getDashboardVisual(dashToEdit);

		if (dashToEdit) {
			if (showDeleteModal) {
				dashToDelete = dashToEdit;
			}

			editDashObj = {
				name: dashToEdit.name,
				...mapDashToAvatar(dashToEdit),
			};

			if (visual) {
				editDashObj[visual.type] = visual.value;
			}
		}
	}

	$: disableAdd =
		!(
			editDashObj?.image?.trim() ||
			editDashObj?.icon?.trim() ||
			editDashObj?.backgroundColor?.trim()
		) || !editDashObj?.name?.trim();

	interface DashboardTab extends TypeTab {
		isDndShadowItem?: boolean;
	}

	let items: DashboardTab[];
	$: items = [...$dashboardTabs].filter((tab) => !checkIsEventLogTab(tab));

	$: listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});
	let uploadedImagesDB = [];
	let imagesByRoomType = [];
	let icons = [
		{
			id: 0,
			icon: 'fat fa-objects-column',
			selected: false,
			type: 'icon',
			isIcon: true,
		},
		{
			id: 1,
			icon: 'fat fa-microchip',
			selected: false,
			type: 'icon',
			isIcon: true,
		},
		{
			id: 2,
			icon: 'fat fa-bolt',
			selected: false,
			type: 'icon',
			isIcon: true,
		},
		{
			id: 3,
			icon: 'fat fa-garage',
			selected: false,
			type: 'icon',
			isIcon: true,
		},
		{
			id: 4,
			icon: 'fat fa-server',
			selected: false,
			type: 'icon',
			isIcon: true,
		},
		{
			id: 5,
			icon: 'fat fa-oven',
			selected: false,
			type: 'icon',
			isIcon: true,
		},
	];

	const onCloseDeleteModal = () => {
		dashToDelete = -1;
		showDeleteModal = false;
	};

	const onCloseModal = () => {
		dashboardsEdit = false;
		listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});
		editDashId = null;
		dispatch('close');
	};

	const onDeleteCard = async () => {
		if ($location.includes('/dash/' + dashToDelete.id)) {
			replace('/dash');
		}
		await deleteDashboard(dashToDelete.id);
		onCloseDeleteModal();
		dashToDelete = null;
	};

	type DashboardVisual =
		| { type: 'image'; value: string }
		| { type: 'icon'; value: string }
		| { type: 'backgroundColor'; value: string }
		| null;

	const getDashboardVisual = (obj: any): DashboardVisual => {
		if (!!obj?.image?.trim() && obj?.image?.trim() !== '/[image_url]') {
			return { type: 'image', value: obj.image.trim() };
		}

		if (!!obj?.icon?.trim()) {
			return { type: 'icon', value: obj.icon.trim() };
		}

		if (!!obj?.backgroundColor?.trim()) {
			return { type: 'backgroundColor', value: obj.backgroundColor.trim() };
		}

		return null;
	};

	const onSave = async () => {
		const visual = getDashboardVisual(editDashObj);
		let id;
		if (isEditDash) {
			id = editDashId;
			requestChangeDashboardName(id, editDashObj.name, visual);
		} else {
			id = await addDashboard(editDashObj.name.trim(), visual);
		}

		editDashObj = {
			name: '',
			image: '',
		};

		const currentDash = $dashboards[id];
		// Default widgets should be created for initial default created dashboard.
		if (!currentDash?.dashWidgets && id?.toString() === DEFAULT_DASH_ID.toString()) {
			await createNewDashWithDefaultWidgets(currentDash, id);
		}

		push(`/dash/${id}`);
		onCloseModal();
	};

	const fetchUploadedImadesDB = async () => {
		uploadedImagesDB = await getImages('dashboard');
	};

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editDashObj = {
			...editDashObj,
			name: target.value,
		};
	};

	const onSelectImage = (e: CustomEvent) => {
		editDashObj = {
			...editDashObj,
			icon: e.detail.icon,
			image: e.detail.image,
			backgroundColor: e.detail.backgroundColor,
		};
	};

	const onUploadImage = (e: CustomEvent) => {
		onSelectImage(e);
		uploadedImagesDB = [...e.detail.uploadedImagesDB];
	};

	$: if (dashboardsEdit) {
		fetchUploadedImadesDB();
	}

	onMount(() => {
		imagesByRoomType = getImagesByType('room', null);
	});
</script>

<!-- Ivo: New Translation for title -->
<Modal
	on:close={onCloseModal}
	showModal={dashboardsEdit}
	title={isEditDash
		? $format('modal_title_edit_dashboard', { default: 'Edit Dashboard' })
		: $format('modal_title_add_dashboard', { default: 'Add New Dashboard' })}
	contentDirection="row"
>
	<svelte:fragment slot="content">
		{#if items.length >= DASHBOARDS_LIMIT}
			<Alert
				customIcon={!isPremium ? 'fas fa-gem icon-premium-color' : ''}
				type="warning"
				heading={$format(alertMessegaLimitTranslation, {
					values: {
						limit: DASHBOARDS_LIMIT,
						premiumLimit: PREMIUM_DASH_TABS_LIMIT,
					},
					default: alertMessageReachedLimitDefault,
				})}
				class="mb-l"
				hideSlotBottom={isPremium}
			>
				<Button
					slot="bottom"
					variant="premium"
					size="m"
					width="auto"
					on:click={() => {
						onCloseModal();
						push('/settings/premium');
					}}
				>
					{capFirstChar(
						$format('button_get_premium', {
							default: 'Get Premium',
						}),
					)}
				</Button>
			</Alert>
		{:else}
			<Alert
				class="mb-l"
				heading={$format('alert_heading_dashboard_created', {
					values: { limit: DASHBOARDS_LIMIT, count: items.length },
					default: `Dashboards created: ${items.length}/${DASHBOARDS_LIMIT}`,
				})}
				message={$format('alert_message_dashboard_created', {
					values: {
						limit: DASHBOARDS_LIMIT,
						plan: plan,
					},
					default: `${plan} allows to create up to ${DASHBOARDS_LIMIT} dashboards.`,
				})}
				isHeadingBold
			/>
		{/if}

		<Container noScroll padding="0">
			<Text class="mb-s text-capitalize text-500" element="h2">
				{$format('label_menu_items_dashboard', { default: 'dashboard' })}&nbsp;{$format('name', {
					default: 'name',
				})}
			</Text>

			<Input
				class="mr-s"
				label={`${$format('label_menu_items_dashboard', { default: 'dashboard' })} ${$format(
					'name',
					{
						default: 'name',
					},
				)}`}
				icon={'fas fa-objects-column'}
				value={editDashObj.name}
				id="dashboard-tab-name-input"
				on:input={onInputName}
				aria-labelledby="dashboard-tab-name-input"
			/>
		</Container>

		<hr class="mt-l mb-l" />

		<Container noScroll padding="0">
			<Text element="h2" class="mb-m text-500">
				{$format('select_image', { default: 'Select image' })}
			</Text>

			<ImageList
				selectedImage={editDashObj.image}
				selectedBackgroundColor={editDashObj.backgroundColor}
				selectedIcon={editDashObj.icon}
				{uploadedImagesDB}
				imagesByTypeDB={imagesByRoomType}
				iconsDB={icons}
				requestType="dashboard"
				on:select-image={onSelectImage}
				on:upload-image={onUploadImage}
			/>
		</Container>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onCloseModal}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			disabled={(items.length >= DASHBOARDS_LIMIT && isEditDash) || disableAdd}
			on:click={(isEditDash || items.length <= DASHBOARDS_LIMIT) && onSave}
		>
			{$format('button_save')}
		</Button>
	</div>
</Modal>

<Modal
	on:close={onCloseDeleteModal}
	showModal={showDeleteModal}
	title={$format('prompt_title_delete_dashboard', {
		default: 'Delete Dashboard?',
	})}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="text-bold text-center mb-s" styleType="controls-large">
				{dashToDelete.name}
			</Text>
			<Text class="width-full">
				{$format('on_delete_item_msg', {
					values: {
						device_name: dashToDelete.name,
						type: $format('label_menu_items_dashboard', {
							default: 'Dashboard',
						}),
					},
					default: `Deleting ${$format('label_menu_items_dashboard', {
						default: 'Dashboard',
					}).toLowerCase()} ‘${dashToDelete.name}’ will permanently remove it from your account.`,
				})}
			</Text>
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseDeleteModal}
			aria-label={$format('aria_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onDeleteCard}
			aria-label={$format('aria_delete_tab', { default: 'Delete tab' })}
		>
			{$format('button_delete_dashboard', { default: 'Delete dashboard' })}
		</Button>
	</div>
</Modal>
